import { useQuery } from "@tanstack/react-query";
import { FileText } from "lucide-react";
import { DialogQueryState } from "@/components/shared/DialogQueryState";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { apiGet } from "@/lib/api-client";
import { formatDate } from "@/lib/format";
import { policyKeys } from "@/lib/query";
import { formEntriesSchema } from "@/schemas/policy-dialogs";

interface EndorsementsDialogProps {
  policyNumber: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EndorsementsDialog({ policyNumber, open, onOpenChange }: EndorsementsDialogProps) {
  const {
    data: forms,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: policyKeys.forms(policyNumber),
    queryFn: () =>
      apiGet(`/api/policies/${encodeURIComponent(policyNumber)}/forms`, formEntriesSchema),
    enabled: open,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Endorsements</DialogTitle>
          <DialogDescription>Policy {policyNumber}</DialogDescription>
        </DialogHeader>

        <DialogQueryState
          isLoading={isLoading}
          isError={isError}
          error={error}
          onRetry={() => refetch()}
          errorMessage="Failed to load endorsements"
          isEmpty={!isLoading && !isError && forms !== undefined && forms.length === 0}
          emptyIcon={FileText}
          emptyMessage="No endorsement forms on record."
          skeletonHeight="h-10"
        />

        {forms && forms.length > 0 && (
          <div className="max-h-[60vh] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-background">
                <tr className="border-b text-left text-muted-foreground">
                  <th className="px-2 py-2 font-medium">Form Number</th>
                  <th className="px-2 py-2 font-medium">Form Name</th>
                  <th className="px-2 py-2 font-medium">Edition</th>
                  <th className="px-2 py-2 font-medium">Effective Date</th>
                </tr>
              </thead>
              <tbody>
                {forms.map((f) => (
                  <tr key={f.id} className="border-b last:border-0">
                    <td className="px-2 py-2 font-mono text-xs">{f.formNumber}</td>
                    <td className="px-2 py-2">{f.formName}</td>
                    <td className="px-2 py-2">{f.formEdition}</td>
                    <td className="px-2 py-2">{formatDate(f.effectiveDate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
